const text = `## MMS Integration Implementation

### PRE-REQUISITES
1. Create a Developer account.
2. Create a license (API keys) and setup callback URLs for required events for specified facilities.

&nbsp

### API Endpoints (Base URLs)
DEV - https://api.developers.lifefitness.com/partnerapi/dev

STAGING - https://api.developers.lifefitness.com/partnerapi/staging

PRODUCTION - https://api.developers.lifefitness.com/partnerapi/production

### 1. Get Authentication Token

Generate an access token for your application using PartnerAPI. The access token returned in the success response is valid for 24 hours and can be used to make API calls to the PartnerAPI endpoints.

~~~
POST /api/v3/oauth/token HTTP/1.1
Host: {{BASE_URL}}
Content-Type: application/json
Accept: application/json
Content-Length: 108

{
    "grantType": "client_credentials",
    "clientId": "0c4b64...",
    "clientSecret": "TUv17HH..."
}
~~~

Success response will return an access token which can be used to make API calls to the PartnerAPI endpoints.
~~~
{
    "status": "SUCCESSFUL",
    "data": {
        "accessToken": "eyAbc...",
        "tokenType": "Bearer",
        "expiresInSeconds": 3600
    },
    "error": null,
    "meta": null
}
~~~

### 2. USER CREATION

Create a new user account. This would create a shadow account for the user in our database and the user can login to the equipment using their credentials. 
Use the access token generated in the previous step as the Bearer token in the Authorization header to make this API call.
    
~~~
POST /api/v3/users HTTP/1.1
Host: {{BASE_URL}}
Accept: application/json
Authorization: Bearer eyAbc...
Content-Type: application/json

{
    "emailAddress": "jane.doe@example.com",
    "xid": "JN10001",
    "dob": "1990-01-30",
    "preferredUnit": "M",
    "facilityIdList": [20],
    "weight": 150.80,
    "weightUnit": "M",
    "password": "Test123!",
    "height": 170.50,
    "heightUnit": "M",
    "firstName": "Jane",
    "lastName": "Doe",
    "gender": "f",
    "preferredLanguageCode": "en"
}
~~~

Success response will return a user access token which can be used to make API calls on behalf of the user. The user access token is valid for 24 hours.
~~~
{
    "status": "SUCCESSFUL",
    "data": {
        "user": {
            "userId": 2132401,
            "xid": "JN10001",
            "age": 35,
            "firstName": "Jane",
            "lastName": "Doe",
            "gender": "F",
            "emailAddress": "jane.doe@example.com",
            "weight": 150.80,
            "weightUnit": "M",
            "height": 170.50,
            "heightUnit": "I",
            "preferredUnit": "M",
            "preferredLanguageCode": "en",
            "enabled": true,
            "dob": "1990-01-30",
            "vo2max": null,
            "userAccessToken": "xyz123...",
            "userRefreshAccessToken": "99162...",
            "userAccessTokenExpiry": "2026-04-28T18:22:55.857+00:00",
            "createdAt": "2025-04-28T00:00:00.000+00:00",
            "facilityIdList": [20]
        }
    },
    "error": null,
    "meta": null
}
~~~

&nbsp

### 3. Connected Cardio Equipment Login

3.1 If you have the equipment bodySerial, then use the following Partner API to login the user to the connected cardio equipment. The user can then start using the equipment.

~~~
POST /api/v3/users/cardio-login HTTP/1.1
Host: {{BASE_URL}}
Accept: application/json
Authorization: Bearer eyAbc...
user-access-token: xyz123...
Content-Type: application/json
Content-Length: 399

{
    "bodySerial": "XYZ123"
}
~~~

3.2 If you have the QR code URL of the connected cardio equipment, then use the following Partner API to login the user to the connected cardio equipment. The user can then start using the equipment.

~~~
POST /api/v3/qr HTTP/1.1
Host: {{BASE_URL}}
Accept: application/json
Authorization: Bearer eyAbc...
user-access-token: xyz123...
Content-Type: application/json
Content-Length: 399

{
 "qrCodeURL": "https://lfconnect.com/q?t=c&l=XYZ123"
}
~~~

Other supported methods for user login to connected cardio equipment are: 
* RFID login 
* NFC login
* Mobile Wallet login (google and ios pass)
* Manual login using email and password on connected cardio equipment
* Login using Life Fitness Connect app
    
&nbsp

### 4. User completes a workout and logs out of the connected cardio equipment.

When a logged-in user completes the workout on the connected cardio equipment, the workout data is saved in the Life Fitness database.

&nbsp

### 5. Workout data sync

5.1 Life Fitness server sends a POST notification to the registered callback URL when new completed workout is saved. The subType is 'Lifefitness' and the notification contains the workout-guid which can be used to retrieve the workout details using PartnerAPI.

~~~
POST https://www.example.com/notification?subType=Lifefitness&timestamp=<timestamp>&access_token=<userAccessToken>&device=<equipmentSerial>&facilityId=<facilityId>>&activityId=<activityId>&guid=<workoutguid>
~~~

&nbsp

5.2 Retrieve workout details using workout-guid retrieved from callback notification by calling the following Partner API using the user access token.
~~~
GET /api/v3/workout-results/lifefitness/{guid} HTTP/1.1
Host: {{BASE_URL}}
Accept: application/json
Authorization: Bearer eyAbc...
user-access-token: xyz123...
~~~

&nbsp

### 6. User Profile changes

6.1. Any changes in the user profile (like weight, preferred unit, name changes etc) triggers a callback with subtype 'Account' to the registered callback URL.

~~~
POST https://www.example.com/notification?subType=Account&access_token=<userAccessToken>
~~~

&nbsp

6.2. Retrieve latest user profile by calling the following Partner API using the user access token.

~~~
GET /api/v3/users HTTP/1.1
Host: {{BASE_URL}}
Accept: application/json
Authorization: Bearer eyAbc...
user-access-token: xyz123...
~~~

&nbsp

### 7. RFID SYNC

7.1. Update an RFID for a user by calling Partner API using the user access token.

~~~
POST /api/v3/users/rfid HTTP/1.1
Host: {{BASE_URL}}
Accept: application/json
Authorization: Bearer eyAbc...
user-access-token: xyz123...
Content-Type: application/json
{
"rfid" : "RFID1"
}
~~~  

&nbsp

7.2. Retrieve list of latest RFIDs assigned to a user by calling Partner API using the user access token.

~~~
GET /api/v3/users/rfid HTTP/1.1
Host: {{BASE_URL}}
Accept: application/json
Authorization: Bearer eyAbc...
user-access-token: xyz123...
~~~

&nbsp

### 8. DELETE USER ACCOUNT

Delete user account from Life Fitness database by calling Partner API using the user access token. This would delete the account permanently from our records.

~~~
DELETE /api/v3/users HTTP/1.1
Host: {{BASE_URL}}
Accept: application/json
Authorization: Bearer eyAbc...
user-access-token: xyz123...
~~~

&nbsp

For full details of our Partner APIs, click [here](https://docs-partnerapi-v3.halo.fitness/#partner-api-v3-reference).
`;

export default text;



// WEBPACK FOOTER //
// ./src/components/guides/MMSIntegration/MMSIntegrationText2.js